<template>
    <div class="uik-slider">
        <UikContentTitle v-if="label">{{ label }}</UikContentTitle>
        <div class="uik-slider__wrapper">
            <div
                v-if="!isRange"
                class="uik-slider__input"
            >
                <UikInput
                    :value="value"
                    type="number"
                    :min="min"
                    :max="max"
                    :disabled="disabled"
                    @input="parseValue($event)"
                />
            </div>
            <div
                v-if="isRange"
                class="uik-slider__input"
            >
                <UikInput
                    v-model="value[0]"
                    type="number"
                    :min="min"
                    :max="Number(value[1] < max ? value[1] : max)"
                    :disabled="disabled"
                    @input="parseValue($event, 0)"
                />
                <UikInput
                    v-model="value[1]"
                    type="number"
                    :min="Number(value[0] > min ? value[0] : min)"
                    :max="max"
                    :disabled="disabled"
                    @input="parseValue($event, 1)"
                />
            </div>
            <button
                ref="slider"
                class="uik-slider__slider"
                :class="{'uik-slider__slider--transitions': transitions}"
                @mousedown="!disabled ? changePos($event) : null"
            >
                <button
                    v-for="i in (isRange ? 2 : 1)"
                    :ref="'point' + i"
                    :key="'point-' + i"
                    :style="point(i)"
                    class="uik-slider__value"
                >
                    <div
                        class="uik-slider__point"
                        :class="{'uik-slider__point--dragging': i === dragging}"
                    />
                </button>
                <div class="uik-slider__line">
                    <div
                        :style="line"
                        class="uik-slider__active-line"
                    />
                </div>
            </button>
        </div>
        <p
            v-if="errorMessage"
            class="uik-slider__error-message"
        >
            {{ errorMessage }}
        </p>
    </div>
</template>

<script>
export default {
    name: "UikSlider",
    model: {
        prop: "value",
        event: "change"
    },
    props: {
        value: { type: [Number, Array, String], default: 0 },
        label: { type: String, default: "" },
        min: { type: Number, default: 0 },
        max: { type: Number, default: 1 },
        step: { type: Number, default: null },
        errorMessage: { type: String, default: "" },
        disabled: { type: Boolean, default: false }
    },
    data () {
        return {
            transitions: true,
            dragging: null
        }
    },
    computed: {
        isRange () {
            return Array.isArray(this.value)
        },
        line () {
            if (this.isRange) {
                if (this.min === this.max) {
                    return "left: 0; width: 0%;"
                }
                let left = (this.value[0] - this.min) * 100 / (this.max - this.min) + "%"
                let width = "calc(" + (this.value[1] - this.min) * 100 / (this.max - this.min) + "% - " + left + ")"
                return "left: " + left + "; width: " + width + ";"
            }
            return "width: " + (this.value - this.min) * 100 / (this.max - this.min) + "%;"
        }
    },
    methods: {
        changePos (event) {
            let slider = this.$refs.slider.getBoundingClientRect()
            let pos = event.clientX - slider.left
            let percentage = 100 * pos / slider.width
            if (percentage < 0) percentage = 0
            if (percentage > 100) percentage = 100
            let value = (this.max - this.min) * percentage / 100 + this.min
            if (this.step) {
                value = Math.round(value / this.step) * this.step
                value = parseFloat(value.toFixed(this.countDecimals(this.step)))
            }
            if (value < this.min) value = this.min
            if (value > this.max) value = this.max

            if (this.isRange) {
                if (this.dragging && this.value[this.dragging - 1] === value) return
                if (this.dragging && this.value[0] === this.value[1]) {
                    if (value > this.value[0]) this.dragging = 2
                    else this.dragging = 1
                } else if (!this.dragging) {
                    // The distances from cursor to point
                    let distanceToPoint1 = Math.abs(this.$refs.point1[0].getBoundingClientRect().left - event.clientX)
                    let distanceToPoint2 = Math.abs(this.$refs.point2[0].getBoundingClientRect().left - event.clientX)
                    this.dragging = distanceToPoint1 < distanceToPoint2 ? 1 : 2
                }
                let newValue = [ ...this.value ]
                newValue[this.dragging - 1] = value
                this.$emit("change", newValue)
            } else {
                if (this.dragging && this.value === value) return
                this.dragging = 1
                this.$emit("change", value)
            }

            document.onmousemove = (e) => {
                this.transitions = false
                this.changePos(e)
            }
            document.onmouseup = () => {
                this.transitions = true
                this.dragging = null
                document.onmousemove = null
                document.onmouseup = null
            }
        },
        point (index) {
            if (this.isRange) {
                if (this.min === this.max) {
                    return "left: calc(50% - 5px);"
                }
                return "left: calc(" + (this.value[index - 1] - this.min) * 100 / (this.max - this.min) + "% - 5px);"
            }
            return "left: calc(" + (this.value - this.min) * 100 / (this.max - this.min) + "% - 5px);"
        },
        parseValue (value, inputIndex) {
            if (value < this.min) value = this.min
            if (value > this.max) value = this.max
            if (this.isRange) {
                let newValue = [...this.value]
                newValue[inputIndex] = value
                this.$emit("change", newValue)
            } else {
                this.$emit("change", value)
            }
        },
        countDecimals (num) {
            if (Math.floor(num) === num) return 0
            return num.toString().split(".")[1].length || 0
        }
    }
}
</script>

<style lang="scss">
.uik-slider {
    min-width: 200px;
    padding-right: 10px;
    button {
      outline: none !important;
    }
    .uik-slider__wrapper {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
        .uik-slider__input {
            display: flex;
            flex-flow: row nowrap;
            justify-content: flex-start;
            align-items: center;
            margin-right: 15px;
            > * + * {
                margin-left: 10px;
            }
            .uik-input__input {
                margin-bottom: 0;
                text-align: center;
                padding: 10px;
                width: 70px;
            }
        }
        .uik-slider__slider {
            position: relative;
            width: 100%;
            display: flex;
            flex-flow: row nowrap;
            justify-content: center;
            align-items: center;
            border: none;
            padding: 10px 0;
            background: transparent;
            &:hover {
                cursor: pointer;
            }
            .uik-slider__line {
                border: none;
                height: 3px;
                width: 100%;
                background: $borderColor;
                .uik-slider__active-line {
                    position: absolute;
                    left: 0;
                    width: 50%;
                    max-width: 100%;
                    height: inherit;
                    background: $primary;
                }
            }
            .uik-slider__value {
                border: none;
                background: transparent;
                width: 11px;
                height: 11px;
                border-radius: 50%;
                position: absolute;
                left: 0;
                z-index: 1;
                display: flex;
                justify-content: center;
                align-items: center;
                pointer-events: none;
                .uik-slider__point {
                    position: absolute;
                    background: $primary;
                    width: 11px;
                    height: 11px;
                    border-radius: 50%;
                    transition: all 0.18s;
                    &--dragging {
                        transform: scale(1.5);
                    }
                }
                &:hover {
                    .uik-slider__point {
                        transform: scale(1.3);
                    }
                }
            }
            &--transitions {
                .uik-slider__line {
                    .uik-slider__active-line {
                        transition: all 0.2s ease-out;
                    }
                }
                .uik-slider__value {
                    transition: all 0.2s ease-out;
                    pointer-events: initial;
                    &:hover {
                        cursor: pointer;
                    }
                }
            }
        }
    }
    .uik-slider__error-message {
        color: $error;
        line-height: 1.375rem;
        margin-top: .5rem;
    }
}
</style>
